
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import ContentSwitcher from '../../../../components/docs/ContentSwitcher';
import Video from 'plaid-threads/Icons/Video';
export const metadata = {
  toc: true,
  layout: 'reference',
  description: 'Learn how to integrate your app with the Link Android SDK. Link enables the connection of your app to financial institutions.',
  preloadImages: [{
    src: '/assets/img/docs/link-android.png',
    type: 'image/webp'
  }]
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const Schema = makeShortcode("Schema");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Link Android SDK' subtitle='Learn how to integrate your app with the Link Android SDK' mdxType="Header" />
    <h4 id="overview" className="no-sticky">Overview</h4>
    <p>{`The Plaid Link SDK is a quick and secure way to link bank accounts to Plaid in your Android app. Link
is a drop-in module that handles connecting a financial institution to your app (credential validation,
multi-factor authentication, error handling, etc.), without passing sensitive personal information to your server.`}</p>
    <p>{`To get started with Plaid Link for Android, clone the
`}<a parentName="p" {...{
        "href": "https://github.com/plaid/plaid-link-android"
      }}>{`GitHub repository`}</a>{` and try out the example application,
which provides a reference implementation in both Java and Kotlin. You'll want to sign up for
`}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/keys"
      }}>{`free API keys`}</a>{` through the Plaid Dashboard to get started.`}</p>
    <Callout icon={<Video mdxType="Video" />} mdxType="Callout">
      <p>{`Prefer to learn by watching? A `}<a parentName="p" {...{
          "href": "https://youtu.be/oM7vL49I5tc"
        }}>{`video guide`}</a>{` is available for this content.`}</p>
    </Callout>
    <h4 id="initial-android-setup" className="no-sticky">Initial Android setup</h4>
    <p>{`Before writing code using the SDK, you must first perform some setup steps to register your app with Plaid and configure your project.`}</p>
    <h5 {...{
      "id": "register-your-app-id"
    }}>{`Register your app ID`}</h5>
    <p>{`To register your Android app ID:`}</p>
    <ol>
      <li parentName="ol">{`Sign in to the `}<a parentName="li" {...{
          "href": "https://dashboard.plaid.com/signin"
        }}>{`Plaid Dashboard`}</a>{`
and go to the `}<a parentName="li" {...{
          "href": "https://dashboard.plaid.com/developers/api"
        }}><strong parentName="a">{`Build -> API`}</strong></a>{` page.`}</li>
      <li parentName="ol">{`Next to `}<strong parentName="li">{`Allowed Android package names`}</strong>{` click `}<strong parentName="li">{`Configure`}</strong>{` then `}<strong parentName="li">{`Add New Android Package Name`}</strong>{`.`}</li>
      <li parentName="ol">{`Enter your package name, for example `}<inlineCode parentName="li">{`com.plaid.example`}</inlineCode>{`.`}</li>
      <li parentName="ol">{`Click `}<strong parentName="li">{`Save Changes`}</strong>{`.`}</li>
    </ol>
    <p>{`Your Android app is now set up and ready to start integrating with the Plaid SDK.`}</p>
    <blockquote>
      <p parentName="blockquote">{`New versions of the Android SDK are released frequently, at least once every few months. Major releases occur annually.
You should keep your version up-to-date to provide the best Plaid Link experience in your application.`}</p>
    </blockquote>
    <h5 {...{
      "id": "update-your-project-plugins"
    }}>{`Update your project plugins`}</h5>
    <p>{`In your root-level (project-level) Gradle file (`}<inlineCode parentName="p">{`build.gradle`}</inlineCode>{`), add rules to include
the Android Gradle plugin. Check that you have Google's Maven repository as well.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-groovy",
        "metastring": "switcher=false #build.gradle (Project-level)",
        "switcher": "false",
        "#build.gradle": true,
        "(Project-level)": true
      }}>{`buildscript {
    repositories {
        // Check that you have the following line (if not, add it):
        google()  // Google's Maven repository
        mavenCentral() // Include to import Plaid Link Android SDK
    }
    dependencies {
        // ...
    }
}
`}</code></pre>
    <h5 {...{
      "id": "add-the-plaidlink-sdk-to-your-app"
    }}>{`Add the PlaidLink SDK to your app`}</h5>
    <p>{`In your module (app-level) Gradle file (usually `}<inlineCode parentName="p">{`app/build.gradle`}</inlineCode>{`), add a line to the bottom of the file.
The latest version of the PlaidLink SDK is `}<img alt="Maven Central" src="https://img.shields.io/maven-central/v/com.plaid.link/sdk-core" />{`
and can be found on `}<a parentName="p" {...{
        "href": "https://search.maven.org/artifact/com.plaid.link/sdk-core"
      }}>{`Maven Central`}</a>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-groovy",
        "metastring": "switcher=false #build.gradle (App-level)",
        "switcher": "false",
        "#build.gradle": true,
        "(App-level)": true
      }}>{`android {
  compileSdkVersion 36
  defaultConfig {
    minSdkVersion 25 // or greater
  }
}

dependencies {
  // ...
  implementation 'com.plaid.link:sdk-core:<insert latest version>'
}
`}</code></pre>
    <h5 {...{
      "id": "enable-camera-support-identity-verification-only"
    }}>{`Enable camera support (Identity Verification only)`}</h5>
    <p>{`If your app uses `}<a parentName="p" {...{
        "href": "/docs/identity-verification/"
      }}>{`Identity Verification`}</a>{`, a user may need to take a picture of identity documentation or a selfie during the Link flow. To support this workflow, the `}<a parentName="p" {...{
        "href": "https://developer.android.com/reference/android/Manifest.permission#CAMERA"
      }}><inlineCode parentName="a">{`CAMERA`}</inlineCode></a>{`, `}<a parentName="p" {...{
        "href": "https://developer.android.com/reference/android/Manifest.permission#WRITE_EXTERNAL_STORAGE"
      }}><inlineCode parentName="a">{`WRITE_EXTERNAL_STORAGE`}</inlineCode></a>{`, `}<a parentName="p" {...{
        "href": "https://developer.android.com/reference/android/Manifest.permission#RECORD_AUDIO"
      }}><inlineCode parentName="a">{`RECORD_AUDIO`}</inlineCode></a>{`, and `}<a parentName="p" {...{
        "href": "https://developer.android.com/reference/android/Manifest.permission#MODIFY_AUDIO_SETTINGS"
      }}><inlineCode parentName="a">{`MODIFY_AUDIO_SETTINGS`}</inlineCode></a>{` permissions need to be added to your application's `}<inlineCode parentName="p">{`AndroidManifest.xml`}</inlineCode>{`. (While Plaid does not record any audio, some older Android devices require these last two permissions to use the camera.) The `}<inlineCode parentName="p">{`WRITE_EXTERNAL_STORAGE`}</inlineCode>{` permission should be limited to Android 9 and below (i.e. maxSdk=28). If these permissions are not granted in an app that uses Identity Verification, the app may crash during Link.`}</p>
    <h4 id="opening-link" className="no-sticky">Opening Link</h4>
    <p>{`Before you can open Link, you need to first create a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` by calling `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` from your backend.
This call should `}<strong parentName="p">{`never`}</strong>{` happen directly from the mobile client, as it risks exposing your API secret.
The `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` call must include the `}<inlineCode parentName="p">{`android_package_name`}</inlineCode>{` parameter, which should match the `}<inlineCode parentName="p">{`applicationId`}</inlineCode>{` from your app-level
`}<inlineCode parentName="p">{`build.gradle`}</inlineCode>{` file. You can learn more about `}<inlineCode parentName="p">{`applicationId`}</inlineCode>{` in Google's `}<a parentName="p" {...{
        "href": "https://developer.android.com/build/configure-app-module"
      }}>{`Android
developer documentation`}</a>{`.`}</p>
    <MDXCodeBlock type='server' title='/link/token/create' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`link_token_create_request = Plaid::LinkTokenCreateRequest.new(
  {
    user: {
      client_user_id: "user-id",
    },
    client_name: 'Plaid Test App',
    products: ['auth'],
    country_codes: ['US'],
    language: "en",
    redirect_uri: nil_if_empty_envvar('PLAID_REDIRECT_URI'),
    webhook: 'https://webhook.sample.com',
    link_customization_name: "default",
    android_package_name: "com.plaid.example"
  }
)
response = client.link_token_create(
  link_token_create_request
)
link_token = response.link_token
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`ctx := context.Background()

user := plaid.LinkTokenCreateRequestUser{
    ClientUserId: "USER_ID_FROM_YOUR_DB",
}
request := plaid.NewLinkTokenCreateRequest(
  "Plaid Test",
  "en",
  []plaid.CountryCode{plaid.COUNTRYCODE_US},
)
request.SetUser(user)
request.SetProducts([]plaid.Products{plaid.PRODUCTS_AUTH})
request.SetLinkCustomizationName("default")
request.SetWebhook("https://webhook-uri.com")
request.SetAndroidPackageName("com.plaid.example")
resp, _, err := client.PlaidApi.LinkTokenCreate(ctx).LinkTokenCreateRequest(*request).Execute()

linkToken := resp.GetLinkToken()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`ctx := context.Background()

user := plaid.LinkTokenCreateRequestUser{ClientUserID: "USER_ID_FROM_YOUR_DB"}
request := &plaid.LinkTokenCreateRequest{
    ClientName:   "Plaid Test",
    Language:     "en",
    CountryCodes: []plaid.CountryCode{plaid.CountryCodeUs},
}
request.User = &user
request.Products = []plaid.Products{plaid.ProductsAuth}
request.LinkCustomizationName = plaid.String("default")
request.Webhook = plaid.String("https://webhook-uri.com")
request.AndroidPackageName = plaid.String("com.plaid.example")
resp, err := client.Link.CreateToken(ctx, request)

linkToken := resp.GetLinkToken()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`String clientUserId = "user-id";

LinkTokenCreateRequestUser user = new LinkTokenCreateRequestUser()
  .clientUserId(clientUserId)
  .legalName("legal name")
  .phoneNumber("4155558888")
  .emailAddress("email@address.com");

LinkTokenCreateRequest request = new LinkTokenCreateRequest()
  .user(user)
  .clientName("Plaid Test App")
  .products(Arrays.asList(Products.AUTH))
  .countryCodes(Arrays.asList(CountryCode.US))
  .language("en")
  .webhook("https://example.com/webhook")
  .linkCustomizationName("default")
  .androidPackageName("com.plaid.example");

Response<LinkTokenCreateResponse> response = client()
  .linkTokenCreate(request)
  .execute();

String linkToken = response.body().getLinkToken();
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`request = LinkTokenCreateRequest(
    products=[Products('auth'), Products('transactions')],
    client_name="Plaid Test App",
    country_codes=[CountryCode('GB')],
    language='en',
    webhook='https://sample-webhook-uri.com',
    link_customization_name='default',
    android_package_name='com.plaid.example',
    user=LinkTokenCreateRequestUser(
        client_user_id='123-test-user-id'
    )
)
# create link token
response = client.link_token_create(request)
link_token = response['link_token']
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const request: LinkTokenCreateRequest = {
  user: {
    client_user_id: 'user-id',
  },
  client_name: 'Plaid Test App',
  products: ['auth', 'transactions'],
  country_codes: ['GB'],
  language: 'en',
  webhook: 'https://sample-web-hook.com',
  android_package_name: 'com.plaid.example'
};
try {
  const response = await plaidClient.linkTokenCreate(request);
  const linkToken = response.data.link_token;
} catch (error) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/link/token/create -H 'Content-Type: application/json' -d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "user": { "client_user_id": "\${UNIQUE_USER_ID}" },
  "client_name": "Plaid App",
  "products": ["auth"],
  "country_codes": ["GB"],
  "language": "en",
  "webhook": "https://sample-web-hook.com",
  "android_package_name": "com.plaid.example"
}'
`}</code></pre>
    </MDXCodeBlock>
    <h5 {...{
      "id": "create-a-linktokenconfiguration"
    }}>{`Create a LinkTokenConfiguration`}</h5>
    <p>{`Each time you open Link, you will need to get a new `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` from your server and create a new
`}<inlineCode parentName="p">{`LinkTokenConfiguration`}</inlineCode>{` object with it.`}</p>
    <MDXCodeBlock type='android' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-kotlin"
        }}>{`val linkTokenConfiguration = linkTokenConfiguration {
  token = "LINK_TOKEN_FROM_SERVER"
}

`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`LinkTokenConfiguration linkTokenConfiguration = new LinkTokenConfiguration.Builder()
    .token("LINK_TOKEN_FROM_SERVER")
    .build();
`}</code></pre>
    </MDXCodeBlock>
    <p>{`The Link SDK runs as a separate `}<inlineCode parentName="p">{`Activity`}</inlineCode>{` within your app. In order to return the result
to your app, it supports both the standard `}<inlineCode parentName="p">{`startActivityForResult`}</inlineCode>{` and `}<inlineCode parentName="p">{`onActivityResult`}</inlineCode>{`
and the `}<inlineCode parentName="p">{`ActivityResultContract`}</inlineCode>{` `}<a parentName="p" {...{
        "href": "https://developer.android.com/training/basics/intents/result"
      }}>{`result APIs`}</a>{`.`}</p>
    <ContentSwitcher.Container id='Link Result Handling' names={["Activity Result APIs", "Standard Activity APIs"]}>
      <ContentSwitcher.Content>
        <h5 {...{
          "id": "register-a-callback-for-an-activity-result"
        }}>{`Register a callback for an Activity Result`}</h5>
        <MDXCodeBlock type="android" mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-kotlin"
            }}>{`private val linkAccountToPlaid =
registerForActivityResult(OpenPlaidLink()) { result ->
  when (result) {
    is LinkSuccess -> /* handle LinkSuccess */
    is LinkExit -> /* handle LinkExit */
  }
}
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java"
            }}>{`private ActivityResultLauncher<PlaidSession> linkAccountToPlaid = registerForActivityResult(new OpenPlaidLink(),
  result -> {
    if (result instanceof LinkSuccess) {
      /* handle LinkSuccess */
    } else {
      /* handle LinkExit */
    }
  }
);
`}</code></pre>
        </MDXCodeBlock>
        <h5 {...{
          "id": "create-a-plaidlinksession"
        }}>{`Create a PlaidLinkSession`}</h5>
        <p>{`Create a `}<inlineCode parentName="p">{`PlaidLinkSession`}</inlineCode>{` - a `}<inlineCode parentName="p">{`PlaidLinkSession`}</inlineCode>{` is a one-time use object used to open a Link session.
Building the session begins preloading Link, so create it as early as possible: it must be ready before
Link opens, and creating it just before opening can have a perceptible impact on Link startup time. Use
the optional `}<inlineCode parentName="p">{`onLoad`}</inlineCode>{` callback in your `}<inlineCode parentName="p">{`LinkTokenConfiguration`}</inlineCode>{` to learn when the session has finished preloading.`}</p>
        <MDXCodeBlock type="android" mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-kotlin"
            }}>{`val session: PlaidLinkSession =
  Plaid.createPlaidLinkSession(this, linkTokenConfiguration)
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java"
            }}>{`PlaidLinkSession session = Plaid.createPlaidLinkSession(this, linkTokenConfiguration);
`}</code></pre>
        </MDXCodeBlock>
        <h5 {...{
          "id": "open-link"
        }}>{`Open Link`}</h5>
        <MDXCodeBlock type="android" mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-kotlin"
            }}>{`linkAccountToPlaid.launch(session)
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java"
            }}>{`linkAccountToPlaid.launch(session);
`}</code></pre>
        </MDXCodeBlock>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <h5 {...{
          "id": "set-up-parsing-in-onactivityresult"
        }}>{`Set up parsing in onActivityResult`}</h5>
        <p>{`Call `}<inlineCode parentName="p">{`Plaid.parseResult`}</inlineCode>{` in your Activity's `}<inlineCode parentName="p">{`onActivityResult`}</inlineCode>{` to parse the result. It returns a
`}<inlineCode parentName="p">{`LinkResult`}</inlineCode>{` if the result was sent from Link, or `}<inlineCode parentName="p">{`null`}</inlineCode>{` otherwise.`}</p>
        <MDXCodeBlock type="android" mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-kotlin"
            }}>{`override fun onActivityResult(requestCode: Int, resultCode: Int, data: Intent?) {
  super.onActivityResult(requestCode, resultCode, data)
  Plaid.parseResult(requestCode, resultCode, data)?.let { result ->
    when (result) {
      is LinkSuccess -> /* handle LinkSuccess */
      is LinkExit -> /* handle LinkExit */
    }
  }
}
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java"
            }}>{`@Override
protected void onActivityResult(int requestCode, int resultCode, @Nullable Intent data) {
  super.onActivityResult(requestCode, resultCode, data);
  LinkResult result = Plaid.parseResult(requestCode, resultCode, data);
  if (result instanceof LinkSuccess) {
    /* handle LinkSuccess */
  } else if (result instanceof LinkExit) {
    /* handle LinkExit */
  }
}
`}</code></pre>
        </MDXCodeBlock>
        <h5 {...{
          "id": "create-a-plaidlinksession-1"
        }}>{`Create a PlaidLinkSession`}</h5>
        <p>{`Create a `}<inlineCode parentName="p">{`PlaidLinkSession`}</inlineCode>{` - a `}<inlineCode parentName="p">{`PlaidLinkSession`}</inlineCode>{` is a one-time use object used to open a Link session.
Building the session begins preloading Link, so create it as early as possible: it must be ready before
Link opens, and creating it just before opening can have a perceptible impact on Link startup time. Use
the optional `}<inlineCode parentName="p">{`onLoad`}</inlineCode>{` callback in your `}<inlineCode parentName="p">{`LinkTokenConfiguration`}</inlineCode>{` to learn when the session has finished preloading.`}</p>
        <MDXCodeBlock type='android' mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-kotlin"
            }}>{`val session: PlaidLinkSession = Plaid.createPlaidLinkSession(this, linkTokenConfiguration)
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java"
            }}>{`PlaidLinkSession session = Plaid.createPlaidLinkSession(this, linkTokenConfiguration);
`}</code></pre>
        </MDXCodeBlock>
        <h5 {...{
          "id": "open-link-1"
        }}>{`Open Link`}</h5>
        <MDXCodeBlock type="android" mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-kotlin"
            }}>{`session.open(this)

`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java"
            }}>{`session.open(this);

`}</code></pre>
        </MDXCodeBlock>
      </ContentSwitcher.Content>
    </ContentSwitcher.Container>
    <p>{`At this point, Link will open, and will trigger the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback if the user successfully completes the Link flow.`}</p>
    <h4 {...{
      "id": "onsuccess"
    }}><inlineCode parentName="h4">{`onSuccess`}</inlineCode></h4>
    <Schema type='link' sdk='android' method='onSuccess' mdxType="Schema">
      <MDXCodeBlock type='android' title='onSuccess example' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-kotlin"
          }}>{`val success = result as LinkSuccess

// Send public_token to your server, exchange for access_token 
// (if using Item-based products)
val publicToken = success.publicToken
val metadata = success.metadata
metadata.accounts.forEach { account ->
  val accountId = account.id
  val accountName = account.name
  val accountMask = account.mask
  val accountSubType = account.subtype
}
val institutionId = metadata.institution?.id
val institutionName = metadata.institution?.name
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`LinkSuccess success = (LinkSuccess) result;

String publicToken = success.getPublicToken();
LinkSuccess.LinkSuccessMetadata metadata = success.getMetadata();
for (LinkAccount account : metadata.getAccounts()) {
  String accountId = account.getId();
  String accountName = account.getName();
  String accountMask = account.getMask();
  LinkAccountSubtype accountSubtype = account.getSubtype();
}
String institutionId = metadata.getInstitution().getId();
String institutionName = metadata.getInstitution().getName();
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <h4 {...{
      "id": "onexit"
    }}><inlineCode parentName="h4">{`onExit`}</inlineCode></h4>
    <Schema type='link' sdk="android" method="onExit" mdxType="Schema">
      <MDXCodeBlock type="android" title='onExit example' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-kotlin"
          }}>{`val exit = result as LinkExit

val error = exit.error
error?.let { err ->
  val errorCode = err.errorCode.json
  val errorMessage = err.errorMessage
  val displayMessage = err.displayMessage
}
val metadata = exit.metadata
val institutionId = metadata.institution?.id
val institutionName = metadata.institution?.name
val linkSessionId = metadata.linkSessionId;
val requestId = metadata.requestId;
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`LinkExit exit = (LinkExit) result;

LinkError error = exit.getError();
String errorCode = error.getErrorCode().getJson();
String errorMessage = error.getErrorMessage();
String displayMessage = error.getDisplayMessage();

LinkExitMetadata metadata = exit.getMetadata();
String institutionId = metadata.getInstitution().getId();
String institutionName = metadata.getInstitution().getName();
String linkSessionId = metadata.getLinkSessionId();
String requestId = metadata.getRequestId();
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <h4 {...{
      "id": "onevent"
    }}><inlineCode parentName="h4">{`onEvent`}</inlineCode></h4>
    <Schema type='link' sdk='android' method='onEvent' mdxType="Schema">
      <MDXCodeBlock type='android' title='onEvent example' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-kotlin"
          }}>{`Plaid.setLinkEventListener { event -> Log.i("Event", event.toString()) }
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`Plaid.setLinkEventListener(event -> {
  Log.i("Event", event.toString());
});
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <h4 {...{
      "id": "submit"
    }}><inlineCode parentName="h4">{`submit()`}</inlineCode></h4>
    <Schema type='link' sdk='android' method='submit' mdxType="Schema">
      <MDXCodeBlock type="android" title='submit() example' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-kotlin"
          }}>{`val submissionData = SubmissionData(phoneNumber = phoneNumber)
layerSession.submit(submissionData)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`SubmissionData submissionData = new SubmissionData(phoneNumber, null, null);
layerSession.submit(submissionData);
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <h4 id="upgrading" className="no-sticky">Upgrading</h4>
    <p>{`The latest version of the SDK is available from `}<a parentName="p" {...{
        "href": "https://github.com/plaid/plaid-link-android"
      }}>{`GitHub`}</a>{`. New versions of the SDK are released frequently. Major releases occur annually. The Link SDK uses Semantic Versioning, ensuring that all non-major releases are non-breaking, backwards-compatible updates. We recommend you update regularly (at least once a quarter, and ideally once a month) to ensure the best Plaid Link experience in your application. `}</p>
    <p>{`SDK versions are supported for two years; with each major SDK release, Plaid will stop officially supporting any previous SDK versions that are more than two years old. While these older versions are expected to continue to work without disruption, Plaid will not provide assistance with unsupported SDK versions. `}</p>
    <Callout mdxType="Callout">
      <p>{`Android SDK 6.0 replaces the handler-based API (`}<inlineCode parentName="p">{`Plaid.create`}</inlineCode>{` / `}<inlineCode parentName="p">{`PlaidHandler`}</inlineCode>{` / `}<inlineCode parentName="p">{`FastOpenPlaidLink`}</inlineCode>{`) with a session-based API (`}<inlineCode parentName="p">{`Plaid.createPlaidLinkSession`}</inlineCode>{`, opened via `}<inlineCode parentName="p">{`OpenPlaidLink`}</inlineCode>{`) and initially raised the minimum supported version to Android 8.0 (API 26); 6.2.0 lowered it to Android 7.1 (API 25). For migration details, see the `}<a parentName="p" {...{
          "href": "https://github.com/plaid/plaid-link-android/blob/master/v6-migration-guide.md"
        }}>{`v6 migration guide`}</a>{`.`}</p>
    </Callout>
    <h4 id="next-steps" className="no-sticky">Next steps</h4>
    <p>{`If you run into problems integrating with Plaid Link on Android, see `}<a parentName="p" {...{
        "href": "/docs/link/android/troubleshooting"
      }}>{`Troubleshooting the Plaid Link Android SDK`}</a>{`.`}</p>
    <p>{`Once you've gotten Link working, see `}<a parentName="p" {...{
        "href": "/docs/link/best-practices/"
      }}>{`Link best practices`}</a>{` for recommendations on further improving the Link flow.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "onsuccess",
        level: 4,
        title: "onSuccess",
      },
{
        id: "onexit",
        level: 4,
        title: "onExit",
      },
{
        id: "onevent",
        level: 4,
        title: "onEvent",
      },
{
        id: "submit",
        level: 4,
        title: "submit()",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
